import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {FileUtils} from "../../utils/file-utils";
import {ReleaseSummarySearchCriteria} from './release-summary-search-criteria';
import {ReleaseSummary} from './release-summary';
import {PersonLogin} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {ReleaseSummaryService} from '../../services/release-summary-service';
import {StringUtils} from '../../utils/string-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import { CurrencyPipe, DatePipe } from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';

@Component({
    selector: 'recent-releases',
    templateUrl: './recent-releases.component.html',
    styleUrls: ['./recent-releases.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatProgressSpinner, CurrencyPipe, DatePipe]
})
export class RecentReleasesComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly releaseSummaryService = inject(ReleaseSummaryService);
  private readonly router = inject(Router);
  readonly orgUnitId = input.required<string>({ alias: 'org-unit-id' });


  private personLogin!: PersonLogin;
  public readonly releaseResults = signal<ReleaseSummary[]>(undefined!);
  public readonly maxPage = signal<number>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public criteria!: ReleaseSummarySearchCriteria;
  public readonly searchComplete = signal<boolean>(undefined!);

  public previousPage(): void {
    this.criteria.pageNumber -= 1;
    this.getResultPage();
  }

  public nextPage(): void {
    this.criteria.pageNumber += 1;
    this.getResultPage();
  }

  public search(): void {
    this.criteria.pageNumber = 1;
    this.criteria.sortColumn = 'UNDEFINED';
    this.criteria.sortDirection = 'ASCENDING';
    this.maxPage.set(this.criteria.pageNumber);
    this.getResultPage();
  }

  public sortResults(columnName: string): void {
    if (this.criteria.sortColumn == columnName) {
      this.criteria.sortDirection = this.isDescending() ? "ASCENDING" : "DESCENDING";
    } else {
      this.criteria.sortColumn = columnName;
      this.criteria.sortDirection = "ASCENDING";
    }
    this.getResultPage();
  }

  isDescending(): boolean {
    return this.criteria.sortDirection == 'DESCENDING';
  }

  public getResultPage(): void {
    if (this.criteria.pageNumber <= this.maxPage()) {
      this.clearStatus();
      this.releaseSummaryService.getRecentReleases(this.criteria).subscribe(results => {
        this.releaseResults.set(results);
        this.isBusy.set(false);
        this.searchComplete.set(true);
        if (results.length > 0) {
          this.maxPage.set(Math.ceil(results[0].recordCount / 20));
        }
      }, (error: any) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
      });
    }
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.searchComplete.set(false);
    this.releaseResults.set(null!);
  }

  private handleError(error: any): void {
    if (error && error.text) {
      this.errorMessage.set(error.text);
    } else {
      this.errorMessage.set("An error has occured.  Please contact customer service");
    }
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.criteria = new ReleaseSummarySearchCriteria();
      this.personLogin = this.personLoginService.getCachedLogin()!;
      if (!this.personLogin) {
        this.router.navigate(['main']);
      } else {
        this.initialize();
      }
    }
  }

  public goToOrderMaster() {
    this.router.navigate(['orders']);
  }

  public exportToExcel() {
    this.releaseSummaryService.getRecentReleasesTemplate(this.criteria.orgUnitId).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, "Recent Releases.xlsx");
    }, error => {
      this.handleError(error);
    });
  }


  private initialize(): void {
    let orgUnitId: string = this.orgUnitId();
    this.criteria.orgUnitId = orgUnitId;
    this.criteria.sortColumn = 'UNDEFINED';
    this.criteria.sortDirection = 'ASCENDING';
    this.search();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['orgUnitId'].firstChange) {
      this.initialize();
    }
  }
}
